import './styles.css';
import * as THREE from 'three';
import { io } from 'socket.io-client';
import { GAME_CONFIG } from '../shared/config.js';
import { ITEM_TEMPLATES } from '../shared/items.js';
import { ABILITIES } from '../shared/content.js';
import { chunkCoords, chunkKey, terrainAt } from '../shared/terrain.js';
import { LANDMARKS, WORLD_PROPS } from '../shared/world-layout.js';

const $ = (selector) => document.querySelector(selector);
const authScreen = $('#auth-screen');
const authForm = $('#auth-form');
const authError = $('#auth-error');
const loadingScreen = $('#loading-screen');
const loadingStatus = $('#loading-status');
const hud = $('#hud');
const chat = $('#chat');
const connectionScreen = $('#connection-screen');
const connectionTitle = $('#connection-title');
const connectionMessage = $('#connection-message');
const reloadButton = $('#reload-button');
const deathScreen = $('#death-screen');
const perfHud = $('#perf-hud');

const UI_SETTINGS_KEY = 'meton3.ui.settings';
const DEFAULT_UI_SETTINGS = Object.freeze({
  audioEnabled: true,
  effectsVolume: 1,
  textScale: 1,
  reducedMotion: null
});
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function readUiSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem(UI_SETTINGS_KEY) || '{}');
    const settings = { ...DEFAULT_UI_SETTINGS, ...(saved && typeof saved === 'object' ? saved : {}) };
    settings.audioEnabled = settings.audioEnabled !== false;
    settings.effectsVolume = Math.max(0, Math.min(1, Number(settings.effectsVolume) || 1));
    settings.textScale = Math.max(0.9, Math.min(1.25, Number(settings.textScale) || 1));
    settings.reducedMotion = typeof settings.reducedMotion === 'boolean' ? settings.reducedMotion : null;
    return settings;
  } catch {
    return { ...DEFAULT_UI_SETTINGS };
  }
}

function writeUiSettings(settings) {
  try {
    localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage can be disabled by privacy settings. UI preferences remain session-local.
  }
}

function systemReducedMotion() {
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

function applyUiSettings(settings) {
  const reducedMotion = settings.reducedMotion === null ? systemReducedMotion() : settings.reducedMotion;
  document.documentElement.dataset.reducedMotion = reducedMotion ? 'true' : 'false';
  document.documentElement.classList.toggle('reduced-motion', reducedMotion);
  document.documentElement.style.setProperty('--ui-text-scale', String(settings.textScale));
  return reducedMotion;
}

function focusableElements(root) {
  if (!root?.querySelectorAll) return [];
  return [...root.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.closest('.hidden')) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

function focusElement(element) {
  if (!(element instanceof HTMLElement)) return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function isSupportedViewport() {
  // The client now adapts its HUD to the available viewport. Keep this helper
  // for the modal/recovery code, but do not block gameplay on screen size.
  return true;
}

let responsiveRequirement;
let previousViewportSupport;

function ensureResponsiveRequirement() {
  responsiveRequirement = responsiveRequirement || $('#unsupported-screen') || $('#viewport-requirement') || $('#responsive-requirement');
  if (!responsiveRequirement) {
    responsiveRequirement = document.createElement('section');
    responsiveRequirement.id = 'unsupported-screen';
    responsiveRequirement.className = 'overlay requirement-overlay hidden';
    const panel = document.createElement('section');
    panel.className = 'loading-card ornate-panel';
    responsiveRequirement.appendChild(panel);
    document.body.appendChild(responsiveRequirement);
  }
  responsiveRequirement.classList.add('overlay', 'requirement-overlay');
  responsiveRequirement.setAttribute('role', 'alertdialog');
  responsiveRequirement.setAttribute('aria-modal', 'true');
  responsiveRequirement.setAttribute('aria-label', 'Desktop viewport required');
  const panel = responsiveRequirement.querySelector('.loading-card') || responsiveRequirement;
  if (!panel.querySelector('[data-requirement-title], #unsupported-title')) {
    const title = document.createElement('h2');
    title.dataset.requirementTitle = 'true';
    title.textContent = 'Desktop viewport required';
    panel.appendChild(title);
  }
  if (!panel.querySelector('[data-requirement-message], #unsupported-message')) {
    const message = document.createElement('p');
    message.dataset.requirementMessage = 'true';
    message.textContent = 'The game adapts its interface to your screen size.';
    panel.appendChild(message);
  }
  const title = panel.querySelector('[data-requirement-title], #unsupported-title');
  if (title) {
    title.tabIndex = -1;
    responsiveRequirement.setAttribute('aria-labelledby', title.id || 'viewport-requirement-title');
    title.id ||= 'viewport-requirement-title';
  }
  responsiveRequirement.dataset.modalDismissable = 'false';
  responsiveRequirement.setAttribute('aria-hidden', responsiveRequirement.classList.contains('hidden') ? 'true' : 'false');
  return responsiveRequirement;
}

function updateResponsiveRequirement() {
  const requirement = ensureResponsiveRequirement();
  previousViewportSupport = true;
  setVisible(requirement, false);
  requirement.setAttribute('aria-hidden', 'true');
  if (app?.setViewportSupported) app.setViewportSupported(true);
  return true;
}

let app = null;
let bootState = 'boot';

if (import.meta.env.MODE === 'test') {
  window.__METON3_TEST__ = {
    state: () => app?.state,
    metrics: () => app?.metrics?.(),
    position: () => ({ x: app?.self?.x ?? 0, y: app?.self?.y ?? 0 }),
    enemyIds: () => [...app?.entities?.enemies?.keys?.() || []].sort(),
    forcePosition: (x, y) => {
      if (!app) return;
      app.self.x = x;
      app.self.y = y;
      app.self.authX = x;
      app.self.authY = y;
    }
  };
}

function setVisible(element, visible) {
  element.classList.toggle('hidden', !visible);
}

async function api(pathname, options = {}) {
  const response = await fetch(pathname, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  const body = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.error || `Request failed (${response.status})`);
  return body;
}

function reportClientError(error, state = bootState) {
  const payload = {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    build: import.meta.env.MODE,
    state
  };
  fetch('/api/client-errors', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(() => {});
}

class ChunkManager {
  constructor(scene, onCenterReady) {
    this.scene = scene;
    this.onCenterReady = onCenterReady;
    this.loaded = new Map();
    this.requested = new Set();
    this.pending = [];
    this.ready = [];
    this.workerBusy = false;
    this.centerKey = null;
    this.materials = [
      new THREE.MeshLambertMaterial({ color: 0x315b63, transparent: true, opacity: 0.88 }),
      new THREE.MeshLambertMaterial({ color: 0x55713b }),
      new THREE.MeshLambertMaterial({ color: 0x76513a }),
      new THREE.MeshLambertMaterial({ color: 0x817563 })
    ];
    this.worker = new Worker(new URL('./terrain-worker.js', import.meta.url), { type: 'module' });
    this.worker.onmessage = ({ data }) => {
      this.workerBusy = false;
      this.ready.push(data);
      this.processNextRequest();
    };
    this.worker.onerror = (event) => reportClientError(new Error(`Terrain worker: ${event.message}`), 'terrain');
  }

  update(playerX, playerY) {
    const center = chunkCoords(playerX, playerY);
    const nextCenterKey = chunkKey(center.x, center.y);
    if (this.centerKey === nextCenterKey) return;
    this.centerKey = nextCenterKey;
    const desired = [];
    for (let y = center.y - GAME_CONFIG.renderDistance; y <= center.y + GAME_CONFIG.renderDistance; y += 1) {
      for (let x = center.x - GAME_CONFIG.renderDistance; x <= center.x + GAME_CONFIG.renderDistance; x += 1) {
        desired.push({ x, y, key: chunkKey(x, y), priority: Math.abs(x - center.x) + Math.abs(y - center.y) });
      }
    }
    desired.sort((a, b) => a.priority - b.priority);
    for (const request of desired) {
      if (!this.loaded.has(request.key) && !this.requested.has(request.key)) {
        this.requested.add(request.key);
        this.pending.push(request);
      }
    }
    this.processNextRequest();

    for (const [key, chunk] of this.loaded) {
      if (Math.abs(chunk.x - center.x) > GAME_CONFIG.renderDistance + 1 ||
          Math.abs(chunk.y - center.y) > GAME_CONFIG.renderDistance + 1) {
        this.disposeChunk(key, chunk);
      }
    }
  }

  processNextRequest() {
    if (this.workerBusy || this.pending.length === 0) return;
    this.workerBusy = true;
    this.worker.postMessage(this.pending.shift());
  }

  uploadOne() {
    const data = this.ready.shift();
    if (!data) return false;
    this.requested.delete(data.key);
    if (this.loaded.has(data.key)) return false;
    const group = new THREE.Group();
    group.position.set(data.x * GAME_CONFIG.chunkSize, data.y * GAME_CONFIG.chunkSize, 0);
    for (let type = 0; type < data.groups.length; type += 1) {
      const values = data.groups[type];
      if (!values?.length) continue;
      const geometry = new THREE.BufferGeometry();
      const interleaved = new THREE.InterleavedBuffer(values, 6);
      geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(interleaved, 3, 0));
      geometry.setAttribute('normal', new THREE.InterleavedBufferAttribute(interleaved, 3, 3));
      geometry.computeBoundingSphere();
      group.add(new THREE.Mesh(geometry, this.materials[type]));
    }
    this.scene.add(group);
    this.loaded.set(data.key, { key: data.key, x: data.x, y: data.y, group });
    if (data.key === this.centerKey) this.onCenterReady();
    return true;
  }

  disposeChunk(key, chunk) {
    this.scene.remove(chunk.group);
    chunk.group.traverse((object) => object.geometry?.dispose());
    this.loaded.delete(key);
  }

  dispose() {
    this.worker.terminate();
    for (const [key, chunk] of this.loaded) this.disposeChunk(key, chunk);
    for (const material of this.materials) material.dispose();
  }
}

class WorldDecor {
  constructor(scene) {
    this.scene = scene;
    this.resources = [];
    this.group = new THREE.Group();
    scene.add(this.group);
    this.build();
  }

  material(color, options = {}) {
    const material = new THREE.MeshLambertMaterial({ color, ...options });
    this.resources.push(material);
    return material;
  }

  instances(geometry, material, props, transform) {
    this.resources.push(geometry);
    const mesh = new THREE.InstancedMesh(geometry, material, props.length);
    const matrix = new THREE.Matrix4();
    props.forEach((prop, index) => {
      const { position, quaternion = new THREE.Quaternion(), scale = new THREE.Vector3(1, 1, 1) } = transform(prop);
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    this.group.add(mesh);
    return mesh;
  }

  build() {
    const houses = WORLD_PROPS.filter((prop) => prop.kind === 'house');
    const trees = WORLD_PROPS.filter((prop) => prop.kind === 'tree');
    const zAt = (prop) => terrainAt(prop.x, prop.y).height;
    this.instances(new THREE.BoxGeometry(105, 82, 58), this.material(0x4f2b1e), houses, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 28 * p.scale), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    const roofGeometry = new THREE.CylinderGeometry(63, 63, 95, 4).rotateX(Math.PI / 2).rotateZ(Math.PI / 4);
    this.instances(roofGeometry, this.material(0x321c19), houses, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 70 * p.scale), scale: new THREE.Vector3(p.scale, p.scale * 0.72, p.scale * 0.32)
    }));
    this.instances(new THREE.BoxGeometry(34, 3, 25), this.material(0xf0a33c, { emissive: 0x9a3e08 }), houses, (p) => ({
      position: new THREE.Vector3(p.x - 35 * p.scale, p.y - 42 * p.scale, zAt(p) + 34 * p.scale), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    this.instances(new THREE.BoxGeometry(132, 9, 5), this.material(0x241312), houses, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 64 * p.scale), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    this.instances(new THREE.CylinderGeometry(7, 10, 48, 6).rotateX(Math.PI / 2), this.material(0x3e2416), trees, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 24 * p.scale), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    this.instances(new THREE.ConeGeometry(34, 95, 7).rotateX(Math.PI / 2), this.material(0x294528), trees, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 78 * p.scale), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    const shrubs = Array.from({ length: 64 }, (_, index) => ({
      x: -820 + ((index * 337) % 2680), y: -820 + ((index * 593) % 2680), scale: 0.65 + (index % 5) * 0.12
    })).filter((p) => Math.hypot(p.x - 500, p.y - 500) > 360 && terrainAt(p.x, p.y).walkable);
    this.instances(new THREE.DodecahedronGeometry(14, 0), this.material(0x425c2c), shrubs, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 9), scale: new THREE.Vector3(p.scale * 1.2, p.scale, p.scale * 0.8)
    }));
    const mountains = Array.from({ length: 14 }, (_, index) => ({
      x: -950 + index * 225, y: index % 2 ? 1920 : 2020, scale: 1.2 + (index % 4) * 0.35
    }));
    this.instances(new THREE.ConeGeometry(110, 300, 6).rotateX(Math.PI / 2), this.material(0x4e4039), mountains, (p) => ({
      position: new THREE.Vector3(p.x, p.y, 125), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    const fires = WORLD_PROPS.filter((prop) => prop.kind === 'campfire' || prop.kind === 'forge');
    this.instances(new THREE.ConeGeometry(13, 34, 6).rotateX(Math.PI / 2), this.material(0xff7626, { emissive: 0x9a2100 }), fires, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 18), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    const arenas = WORLD_PROPS.filter((prop) => prop.kind === 'warden-arena');
    this.instances(new THREE.TorusGeometry(112, 4, 6, 32), this.material(0x922b31, { emissive: 0x3e080e }), arenas, (p) => ({
      position: new THREE.Vector3(p.x, p.y, zAt(p) + 2), scale: new THREE.Vector3(p.scale, p.scale, p.scale)
    }));
    const banners = [[180, 100], [840, 120], [150, 820], [890, 850], [490, 30]].map(([x, y], index) => ({ x, y, index }));
    this.instances(new THREE.BoxGeometry(5, 5, 85), this.material(0x392419), banners, (p) => ({ position: new THREE.Vector3(p.x, p.y, terrainAt(p.x, p.y).height + 42) }));
    this.instances(new THREE.PlaneGeometry(34, 65), this.material(0x761f20, { side: THREE.DoubleSide }), banners, (p) => ({
      position: new THREE.Vector3(p.x + 18, p.y, terrainAt(p.x, p.y).height + 58), quaternion: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2)
    }));
    const gateParts = [
      { x: 445, y: 70, sx: 12, sy: 12, sz: 120, z: 58 },
      { x: 575, y: 70, sx: 12, sy: 12, sz: 120, z: 58 },
      { x: 510, y: 70, sx: 155, sy: 15, sz: 16, z: 112 },
      { x: 510, y: 70, sx: 185, sy: 22, sz: 11, z: 132 }
    ];
    this.instances(new THREE.BoxGeometry(1, 1, 1), this.material(0x402116), gateParts, (p) => ({
      position: new THREE.Vector3(p.x, p.y, terrainAt(p.x, p.y).height + p.z), scale: new THREE.Vector3(p.sx, p.sy, p.sz)
    }));
  }

  dispose() {
    this.scene.remove(this.group);
    this.group.traverse((object) => object.dispose?.());
    this.resources.forEach((resource) => resource.dispose());
  }
}

class EntityRenderer {
  constructor(scene) {
    this.scene = scene;
    this.remotePlayers = new Map();
    this.enemies = new Map();
    this.items = new Map();
    this.shared = {
      bodyGeometry: new THREE.CylinderGeometry(12, 17, 37, 6).rotateX(Math.PI / 2),
      headGeometry: new THREE.SphereGeometry(9, 8, 6),
      shoulderGeometry: new THREE.BoxGeometry(34, 9, 8),
      swordGeometry: new THREE.BoxGeometry(4, 6, 52),
      localMaterial: new THREE.MeshLambertMaterial({ color: 0x8b2522 }),
      remoteMaterial: new THREE.MeshLambertMaterial({ color: 0x4d6684 }),
      armorMaterial: new THREE.MeshLambertMaterial({ color: 0x302b2a }),
      brassMaterial: new THREE.MeshLambertMaterial({ color: 0xc49a4e }),
      bladeMaterial: new THREE.MeshLambertMaterial({ color: 0xbec5c3 }),
      ashHoundGeometry: new THREE.ConeGeometry(15, 42, 5).rotateZ(Math.PI / 2),
      ridgeWolfGeometry: new THREE.ConeGeometry(18, 50, 5).rotateZ(Math.PI / 2),
      raiderGeometry: new THREE.DodecahedronGeometry(24, 0),
      monolithGeometry: new THREE.ConeGeometry(30, 84, 5).rotateX(Math.PI / 2),
      wardenGeometry: new THREE.DodecahedronGeometry(38, 1),
      ashHoundMaterial: new THREE.MeshLambertMaterial({ color: 0x5a3c2c }),
      ridgeWolfMaterial: new THREE.MeshLambertMaterial({ color: 0x6b7168 }),
      raiderMaterial: new THREE.MeshLambertMaterial({ color: 0x82452c }),
      monolithMaterial: new THREE.MeshLambertMaterial({ color: 0x421224, emissive: 0x4f0711 }),
      wardenMaterial: new THREE.MeshLambertMaterial({ color: 0xb52e2b, emissive: 0x5e0d12 }),
      itemGeometry: new THREE.OctahedronGeometry(10, 0),
      goldMaterial: new THREE.MeshLambertMaterial({ color: 0xf4c84b, emissive: 0x604000 }),
      consumableMaterial: new THREE.MeshLambertMaterial({ color: 0xd83d45, emissive: 0x4a0707 }),
      equipmentMaterial: new THREE.MeshLambertMaterial({ color: 0xd1a45b, emissive: 0x3a2100 }),
      emberMaterial: new THREE.MeshLambertMaterial({ color: 0x7ff2cc, emissive: 0x185f4e })
    };
    this.enemyMeshes = {
      ashHound: this.createInstanced(this.shared.ashHoundGeometry, this.shared.ashHoundMaterial, 128),
      ridgeWolf: this.createInstanced(this.shared.ridgeWolfGeometry, this.shared.ridgeWolfMaterial, 128),
      redTuskRaider: this.createInstanced(this.shared.raiderGeometry, this.shared.raiderMaterial, 128),
      dragonMonolith: this.createInstanced(this.shared.monolithGeometry, this.shared.monolithMaterial, 8),
      crimsonWarden: this.createInstanced(this.shared.wardenGeometry, this.shared.wardenMaterial, 2)
    };
    this.itemMeshes = {
      gold: this.createInstanced(this.shared.itemGeometry, this.shared.goldMaterial, 128),
      consumable: this.createInstanced(this.shared.itemGeometry, this.shared.consumableMaterial, 128),
      equipment: this.createInstanced(this.shared.itemGeometry, this.shared.equipmentMaterial, 128),
      ember: this.createInstanced(this.shared.itemGeometry, this.shared.emberMaterial, 128)
    };
    this.selfObject = this.createPlayerObject(this.shared.localMaterial, null);
    scene.add(this.selfObject);
    this.matrix = new THREE.Matrix4();
    this.quaternion = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 4);
    this.scale = new THREE.Vector3(1, 1, 1);
    this.targetRing = new THREE.Mesh(
      new THREE.RingGeometry(22, 27, 32),
      new THREE.MeshBasicMaterial({ color: 0xd9271e, side: THREE.DoubleSide, transparent: true, opacity: 0.82 })
    );
    this.targetRing.visible = false;
    this.scene.add(this.targetRing);
    this.selectedId = null;
  }

  createInstanced(geometry, material, count) {
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.count = 0;
    mesh.userData.capacity = count;
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    return mesh;
  }

  createNameSprite(name) {
    if (!name) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const context = canvas.getContext('2d');
    context.fillStyle = 'rgba(0,0,0,.65)';
    context.fillRect(8, 8, 240, 48);
    context.fillStyle = '#fff';
    context.font = '700 26px system-ui';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, 128, 32);
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(75, 19, 1);
    sprite.position.z = 50;
    sprite.userData.disposable = true;
    return sprite;
  }

  createPlayerObject(material, name) {
    const group = new THREE.Group();
    const body = new THREE.Mesh(this.shared.bodyGeometry, material);
    body.position.z = 25;
    const head = new THREE.Mesh(this.shared.headGeometry, this.shared.armorMaterial);
    head.position.z = 51;
    const shoulders = new THREE.Mesh(this.shared.shoulderGeometry, this.shared.brassMaterial);
    shoulders.position.z = 38;
    const sword = new THREE.Mesh(this.shared.swordGeometry, this.shared.bladeMaterial);
    sword.position.set(18, 0, 24);
    sword.rotation.y = -0.3;
    group.add(body, head, shoulders, sword);
    const label = this.createNameSprite(name);
    if (label) group.add(label);
    return group;
  }

  setSelf(x, y) {
    this.selfObject.position.set(x, y, terrainAt(x, y).height);
  }

  select(id) { this.selectedId = id; }

  enemyAtScreen(clientX, clientY, camera, width, height) {
    let best = null;
    for (const enemy of this.enemies.values()) {
      const point = new THREE.Vector3(enemy.x, enemy.y, terrainAt(enemy.x, enemy.y).height + 30).project(camera);
      const x = (point.x * 0.5 + 0.5) * width;
      const y = (-point.y * 0.5 + 0.5) * height;
      const distance = Math.hypot(clientX - x, clientY - y);
      if (distance < 42 && (!best || distance < best.distance)) best = { enemy, distance };
    }
    return best?.enemy || null;
  }

  nearestItem(x, y, range = 90) {
    return [...this.items.values()].map((item) => ({ item, distance: Math.hypot(item.x - x, item.y - y) }))
      .filter(({ item, distance }) => item.type !== 'gold' && distance <= range)
      .sort((a, b) => a.distance - b.distance)[0]?.item || null;
  }

  updateRemotePlayers(players) {
    const seen = new Set();
    for (const player of players) {
      seen.add(player.id);
      let state = this.remotePlayers.get(player.id);
      if (!state) {
        const object = this.createPlayerObject(this.shared.remoteMaterial, player.name);
        object.position.set(player.x, player.y, terrainAt(player.x, player.y).height);
        this.scene.add(object);
        state = { object, x: player.x, y: player.y, targetX: player.x, targetY: player.y };
        this.remotePlayers.set(player.id, state);
      }
      state.targetX = player.x;
      state.targetY = player.y;
    }
    for (const [id, state] of this.remotePlayers) {
      if (!seen.has(id)) {
        this.disposePlayer(state.object);
        this.remotePlayers.delete(id);
      }
    }
  }

  updateEnemies(enemies) {
    const seen = new Set();
    for (const enemy of enemies) {
      seen.add(enemy.id);
      const current = this.enemies.get(enemy.id);
      if (current) {
        current.targetX = enemy.x;
        current.targetY = enemy.y;
        current.health = enemy.health;
        current.phase = enemy.phase;
        current.facingX = enemy.facingX;
        current.facingY = enemy.facingY;
      } else {
        this.enemies.set(enemy.id, {
          ...enemy,
          x: enemy.x,
          y: enemy.y,
          targetX: enemy.x,
          targetY: enemy.y
        });
      }
    }
    for (const id of this.enemies.keys()) {
      if (!seen.has(id)) this.enemies.delete(id);
    }
  }

  updateItems(items) {
    const seen = new Set();
    for (const item of items) {
      seen.add(item.id);
      this.items.set(item.id, item);
    }
    for (const id of this.items.keys()) {
      if (!seen.has(id)) this.items.delete(id);
    }
  }

  render(dt, elapsed) {
    const interpolation = 1 - Math.exp(-12 * dt);
    for (const state of this.remotePlayers.values()) {
      state.x += (state.targetX - state.x) * interpolation;
      state.y += (state.targetY - state.y) * interpolation;
      state.object.position.set(state.x, state.y, terrainAt(state.x, state.y).height);
    }
    for (const state of this.enemies.values()) {
      state.x += (state.targetX - state.x) * interpolation;
      state.y += (state.targetY - state.y) * interpolation;
    }

    for (const [type, mesh] of Object.entries(this.enemyMeshes)) {
      let index = 0;
      for (const enemy of this.enemies.values()) {
        if (enemy.type !== type || index >= mesh.userData.capacity) continue;
        const height = terrainAt(enemy.x, enemy.y).height;
        this.matrix.compose(
          new THREE.Vector3(enemy.x, enemy.y, height + 24),
          this.quaternion,
          this.scale
        );
        mesh.setMatrixAt(index, this.matrix);
        index += 1;
      }
      mesh.count = index;
      mesh.instanceMatrix.needsUpdate = true;
    }

    const selected = this.enemies.get(this.selectedId);
    this.targetRing.visible = Boolean(selected);
    if (selected) {
      this.targetRing.position.set(selected.x, selected.y, terrainAt(selected.x, selected.y).height + 1.5);
      this.targetRing.rotation.x = 0;
      this.targetRing.scale.setScalar(selected.type === 'dragonMonolith' ? 1.7 : 1);
    }

    for (const [type, mesh] of Object.entries(this.itemMeshes)) {
      let index = 0;
      for (const item of this.items.values()) {
        if (item.type !== type || index >= mesh.userData.capacity) continue;
        const height = terrainAt(item.x, item.y).height;
        const bob = Math.sin(elapsed * 3 + index) * 3;
        this.matrix.compose(
          new THREE.Vector3(item.x, item.y, height + 14 + bob),
          new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), elapsed + index),
          this.scale
        );
        mesh.setMatrixAt(index, this.matrix);
        index += 1;
      }
      mesh.count = index;
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  disposePlayer(object) {
    this.scene.remove(object);
    object.traverse((child) => {
      if (!child.userData.disposable) return;
      child.material?.map?.dispose();
      child.material?.dispose();
    });
  }

  dispose() {
    this.disposePlayer(this.selfObject);
    for (const state of this.remotePlayers.values()) this.disposePlayer(state.object);
    for (const mesh of [...Object.values(this.enemyMeshes), ...Object.values(this.itemMeshes)]) {
      this.scene.remove(mesh);
      mesh.dispose();
    }
    for (const resource of Object.values(this.shared)) resource.dispose?.();
    this.targetRing.geometry.dispose();
    this.targetRing.material.dispose();
    this.scene.remove(this.targetRing);
  }
}

class FrontierAudio {
  constructor(settings = {}) {
    this.context = null;
    this.enabled = settings.audioEnabled !== false;
    this.effectsVolume = Math.max(0, Math.min(1, Number(settings.effectsVolume) || 1));
  }
  start() {
    if (!this.enabled) return;
    this.context ||= new AudioContext();
    this.context.resume().catch(() => {});
  }
  tone(frequency, duration = 0.12, type = 'sine', volume = 0.04, slide = 0) {
    if (!this.enabled) return;
    this.start();
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(25, frequency + slide), now + duration);
    gain.gain.setValueAtTime(volume * this.effectsVolume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
  sword() { this.tone(180, 0.1, 'sawtooth', 0.035, 700); }
  skill() { this.tone(120, 0.22, 'sawtooth', 0.05, 900); setTimeout(() => this.tone(430, 0.18, 'triangle', 0.035, -100), 60); }
  dash() { this.tone(260, 0.12, 'triangle', 0.03, 220); }
  hit() { this.tone(90, 0.13, 'square', 0.035, -55); }
  pickup() { this.tone(440, 0.08, 'sine', 0.03, 220); setTimeout(() => this.tone(660, 0.1, 'sine', 0.025, 180), 65); }
  monolith() { this.tone(58, 0.6, 'sawtooth', 0.05, -20); }
  boss() { this.tone(42, 0.9, 'sawtooth', 0.06, -18); }
  level() { [330, 440, 550, 660].forEach((note, index) => setTimeout(() => this.tone(note, 0.35, 'triangle', 0.035, 80), index * 100)); }
  toggle() { this.enabled = !this.enabled; if (!this.enabled) this.context?.suspend(); else this.start(); return this.enabled; }
  dispose() { this.context?.close(); }
}

class GameClient {
  constructor(player) {
    this.player = player;
    this.state = 'loading';
    this.socket = null;
    this.settings = readUiSettings();
    this.reduceMotion = applyUiSettings(this.settings);
    this.viewportSupported = isSupportedViewport();
    this.modalStack = [];
    this.itemCooldowns = new Map();
    this.keys = new Set();
    this.attackQueued = false;
    this.skillQueued = false;
    this.dashQueued = false;
    this.selectedTargetId = null;
    this.clickDestination = null;
    this.lastAutoAttack = 0;
    this.inventory = { slots: Array(20).fill(null), equipment: {}, quickslots: Array(8).fill(null) };
    this.progression = { step: 0, embers: 0, objective: null, upgradesBySlot: { weapon: 0, armor: 0, charm: 0 } };
    this.abilityReadyAt = { dragonArc: 0, ashStep: 0 };
    this.audio = new FrontierAudio(this.settings);
    this.sequence = 0;
    this.inputTimer = null;
    this.raf = null;
    this.lastFrame = performance.now();
    this.startedAt = performance.now();
    this.frameTimes = [];
    this.longTasks = 0;
    this.lastHudUpdate = 0;
    this.worldReady = false;
    this.terrainReady = false;
    this.self = {
      x: player.x,
      y: player.y,
      authX: player.x,
      authY: player.y,
      health: player.health,
      maxHealth: GAME_CONFIG.player.baseHealth,
      level: player.level,
      xp: player.xp,
      xpToLevel: 500,
      gold: player.gold,
      embers: 0,
      progression: this.progression,
      upgrades: this.progression.upgradesBySlot,
      abilities: {}
    };
    this.setupScene();
    this.setupInput();
    this.setupUI();
    this.setupDiagnostics();
    this.setViewportSupported(this.viewportSupported);
    this.connect();
    this.raf = requestAnimationFrame((time) => this.frame(time));
  }

  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xc97955);
    this.scene.fog = new THREE.Fog(0xb46f51, 1000, 1900);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, innerWidth < 768 ? 1.25 : 1.75));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    $('#canvas-root').appendChild(this.renderer.domElement);

    const aspect = innerWidth / innerHeight;
    this.viewSize = 400;
    this.camera = new THREE.OrthographicCamera(
      -this.viewSize * aspect,
      this.viewSize * aspect,
      this.viewSize,
      -this.viewSize,
      0.1,
      5000
    );
    this.isoDirection = new THREE.Vector3(0.6, 0.6, 0.53).normalize();
    this.camera.up.set(0, 0, 1);
    this.camera.position.copy(this.isoDirection).multiplyScalar(900);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateMatrixWorld();
    this.moveRight = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
    this.moveUp = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 2).negate();
    this.moveRight.z = 0;
    this.moveUp.z = 0;
    this.moveRight.normalize();
    this.moveUp.normalize();

    this.scene.add(new THREE.HemisphereLight(0xffd3a0, 0x2c241c, 1.35));
    const sun = new THREE.DirectionalLight(0xffc47e, 1.8);
    sun.position.set(700, 500, 900);
    this.scene.add(sun);

    this.entities = new EntityRenderer(this.scene);
    this.decor = new WorldDecor(this.scene);
    this.entities.setSelf(this.self.x, this.self.y);
    this.chunks = new ChunkManager(this.scene, () => this.onTerrainReady());
    this.chunks.update(this.self.x, this.self.y);
    this.raycaster = new THREE.Raycaster();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    this.renderer.domElement.addEventListener('pointerdown', (event) => this.onWorldPointer(event));
    this.renderer.domElement.addEventListener('wheel', (event) => {
      event.preventDefault();
      this.viewSize = Math.max(300, Math.min(550, this.viewSize + Math.sign(event.deltaY) * 35));
      this.resize();
    }, { passive: false });

    this.renderer.domElement.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.state = 'graphics-lost';
      bootState = this.state;
      connectionTitle.textContent = 'Graphics context lost';
      connectionMessage.textContent = 'The browser reset WebGL. Reload to rebuild the world safely.';
      setVisible(reloadButton, true);
      this.showConnectionScreen();
      reportClientError(new Error('WebGL context lost'), this.state);
    });
    this.renderer.domElement.addEventListener('webglcontextrestored', () => location.reload());
  }

  setupInput() {
    this.onKeyDown = (event) => {
      if (!this.viewportSupported) return;
      const typing = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
      if (typing) {
        if (event.key === 'Escape') event.target.blur();
        return;
      }
      const interactive = event.target instanceof HTMLElement && event.target.closest('button, a, select, [role="button"], [contenteditable="true"]');
      if (interactive) return;
      const topModal = this.topModal();
      if (topModal && !topModal.contains(event.target)) return;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(event.code)) {
        event.preventDefault();
      }
      if (event.code === 'Space' && !event.repeat) this.attackQueued = true;
      if (event.code === 'KeyQ' && !event.repeat) this.skillQueued = true;
      if ((event.code === 'ShiftLeft' || event.code === 'ShiftRight') && !event.repeat) this.dashQueued = true;
      if (event.code === 'KeyH' && !event.repeat) this.returnHome();
      if (event.code === 'KeyI' && !event.repeat) this.toggleInventory();
      if (event.code === 'KeyF' && !event.repeat) this.pickupNearest();
      if (/^Digit[1-8]$/.test(event.code) && !event.repeat) this.useQuickslot(Number(event.code.slice(-1)) - 1);
      if (event.code === 'Escape') {
        this.closeModal($('#inventory-panel'));
        this.hideTooltip();
        this.clickDestination = null;
        this.selectedTargetId = null;
        this.entities.select(null);
      }
      if (event.code === 'KeyP' && !event.repeat) setVisible(perfHud, perfHud.classList.contains('hidden'));
      if (event.key === 'Enter') {
        event.preventDefault();
        $('#chat-input').focus();
      }
      this.keys.add(event.code);
    };
    this.onKeyUp = (event) => this.keys.delete(event.code);
    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('blur', () => this.keys.clear());
    document.addEventListener('visibilitychange', () => {
      this.keys.clear();
      this.lastFrame = performance.now();
    });
    this.onResize = () => {
      this.resize();
      this.setViewportSupported(true);
      this.updateControlHint();
      updateResponsiveRequirement();
    };
    addEventListener('resize', this.onResize);
  }

  onWorldPointer(event) {
    if (!this.viewportSupported || this.state !== 'playing' || event.button !== 0) return;
    this.audio.start();
    const enemy = this.entities.enemyAtScreen(event.clientX, event.clientY, this.camera, innerWidth, innerHeight);
    if (enemy) {
      this.selectedTargetId = enemy.id;
      this.entities.select(enemy.id);
      const distance = Math.hypot(enemy.x - this.self.x, enemy.y - this.self.y);
      if (distance <= GAME_CONFIG.player.attackRange) this.attackQueued = true;
      else this.clickDestination = { x: enemy.x, y: enemy.y, stopAt: GAME_CONFIG.player.attackRange * 0.78 };
      return;
    }
    const pointer = new THREE.Vector2(event.clientX / innerWidth * 2 - 1, -(event.clientY / innerHeight) * 2 + 1);
    this.raycaster.setFromCamera(pointer, this.camera);
    const point = new THREE.Vector3();
    if (this.raycaster.ray.intersectPlane(this.groundPlane, point)) {
      const x = Math.max(GAME_CONFIG.zone.minX + 10, Math.min(GAME_CONFIG.zone.maxX - 10, point.x));
      const y = Math.max(GAME_CONFIG.zone.minY + 10, Math.min(GAME_CONFIG.zone.maxY - 10, point.y));
      if (terrainAt(x, y).walkable) this.clickDestination = { x, y, stopAt: 8 };
      this.selectedTargetId = null;
      this.entities.select(null);
    }
  }

  topModal() {
    const modal = this.modalStack.at(-1)?.element;
    return modal && !modal.classList.contains('hidden') ? modal : null;
  }

  setupModal(element, { dismissable = false, labelledBy = null } = {}) {
    if (!element) return;
    element.setAttribute('role', element.getAttribute('role') || 'dialog');
    element.setAttribute('aria-modal', 'true');
    element.setAttribute('aria-hidden', element.classList.contains('hidden') ? 'true' : 'false');
    element.dataset.modalDismissable = String(dismissable);
    if (labelledBy) element.setAttribute('aria-labelledby', labelledBy);
    if (!element.hasAttribute('tabindex')) element.tabIndex = -1;
    if (element.classList.contains('hidden')) element.inert = true;
  }

  openModal(element, initialFocus = null) {
    if (!element) return;
    const wasHidden = element.classList.contains('hidden');
    const existing = this.modalStack.find((entry) => entry.element === element);
    if (!existing) {
      this.modalStack.push({
        element,
        returnFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null,
        initialFocus
      });
    } else if (initialFocus) {
      existing.initialFocus = initialFocus;
    }
    element.inert = false;
    element.classList.remove('hidden');
    element.setAttribute('aria-hidden', 'false');
    const focusTarget = typeof initialFocus === 'string' ? element.querySelector(initialFocus) : initialFocus;
    if (wasHidden || !existing) queueMicrotask(() => focusElement(focusTarget || focusableElements(element)[0] || element));
  }

  closeModal(element, { restoreFocus = true } = {}) {
    if (!element) return;
    const index = this.modalStack.findIndex((entry) => entry.element === element);
    const wasTop = index === this.modalStack.length - 1;
    const entry = index >= 0 ? this.modalStack[index] : null;
    if (index >= 0) this.modalStack.splice(index, 1);
    element.classList.add('hidden');
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
    if (element.id === 'inventory-panel') $('#inventory-button')?.setAttribute('aria-expanded', 'false');
    this.hideTooltip();
    if (restoreFocus && wasTop) {
      const next = this.topModal();
      if (next) focusElement(focusableElements(next)[0] || next);
      else if (entry?.returnFocus && document.contains(entry.returnFocus)) focusElement(entry.returnFocus);
    }
  }

  onModalKeyDown(event) {
    const modal = this.topModal();
    if (!modal) return;
    if (event.code === 'KeyI' && !event.repeat && modal.id === 'inventory-panel') {
      event.preventDefault();
      event.stopPropagation();
      this.closeModal(modal);
      $('#inventory-button')?.setAttribute('aria-expanded', 'false');
      return;
    }
    if (event.key === 'Escape' && modal.dataset.modalDismissable === 'true') {
      event.preventDefault();
      event.stopPropagation();
      this.closeModal(modal);
      return;
    }
    if (event.key !== 'Tab') {
      if (!modal.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        focusElement(focusableElements(modal)[0] || modal);
      }
      return;
    }
    const elements = focusableElements(modal);
    if (!elements.length) {
      event.preventDefault();
      focusElement(modal);
      return;
    }
    const first = elements[0];
    const last = elements.at(-1);
    if (event.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
      event.preventDefault();
      focusElement(last);
    } else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
      event.preventDefault();
      focusElement(first);
    }
  }

  setViewportSupported(supported) {
    this.viewportSupported = supported;
    const requirement = ensureResponsiveRequirement();
    this.setupModal(requirement);
    if (supported) this.closeModal(requirement);
    else this.openModal(requirement, requirement.querySelector('[data-requirement-title]'));
  }

  showDeathScreen() {
    document.body.classList.add('game-dead');
    this.openModal(deathScreen, '#respawn-button');
  }

  hideDeathScreen() {
    document.body.classList.remove('game-dead');
    this.closeModal(deathScreen);
  }

  showConnectionScreen() {
    this.openModal(connectionScreen, '#reload-button:not(.hidden), #connection-title');
  }

  hideConnectionScreen() {
    this.closeModal(connectionScreen);
  }

  setupUI() {
    $('#player-label').textContent = this.player.username;
    this.setupModal($('#inventory-panel'), { dismissable: true, labelledBy: 'inventory-title' });
    this.setupModal(deathScreen, { dismissable: false, labelledBy: 'death-title' });
    this.setupModal(connectionScreen, { dismissable: false, labelledBy: 'connection-title' });
    const tooltip = $('#item-tooltip');
    if (tooltip) {
      tooltip.setAttribute('role', 'tooltip');
      tooltip.setAttribute('aria-hidden', 'true');
    }
    const chatInput = $('#chat-input');
    chatInput?.setAttribute('aria-label', 'Chat message');
    this.onModalKeyDownListener = (event) => this.onModalKeyDown(event);
    document.addEventListener('keydown', this.onModalKeyDownListener, true);
    $('#respawn-button').addEventListener('click', () => {
      this.socket?.timeout(2000).emit('player:respawn', (error, result) => {
        if (error) this.toast('The frontier did not answer. Try again.');
        else if (result?.ok) this.hideDeathScreen();
        else this.toast(result?.error || 'Revival was rejected.');
      });
    });
    $('#chat-form').addEventListener('submit', (event) => {
      event.preventDefault();
      const input = $('#chat-input');
      const message = input.value.trim();
      if (!message || !this.socket?.connected) return;
      this.socket.timeout(2000).emit('chat:send', { message }, (error, result) => {
        if (!error && result?.ok) input.value = '';
        else if (error || result?.error) this.toast(result?.error || 'Message could not be sent.');
      });
      input.blur();
    });
    reloadButton.addEventListener('click', () => location.reload(), { once: true });
    $('#inventory-button').addEventListener('click', () => this.toggleInventory());
    $('#inventory-button').setAttribute('aria-expanded', 'false');
    $('#inventory-close').addEventListener('click', () => this.closeModal($('#inventory-panel')));
    $('#active-skill')?.addEventListener('click', () => { this.skillQueued = true; this.audio.start(); });
    $('#forge-action')?.addEventListener('click', () => this.requestForge());
    $('#mastery-claim')?.addEventListener('click', () => this.claimMastery());
    const audioButton = $('#audio-button');
    audioButton?.setAttribute('aria-pressed', String(this.audio.enabled));
    audioButton?.setAttribute('aria-label', this.audio.enabled ? 'Mute sound' : 'Enable sound');
    audioButton?.addEventListener('click', () => {
      const enabled = this.audio.toggle();
      this.settings.audioEnabled = enabled;
      writeUiSettings(this.settings);
      this.updateAudioButton();
    });
    this.buildInventoryUI();
    this.buildQuickbar();
    this.updateAudioButton();
    this.updateControlHint();
  }

  updateControlHint() {
    const controls = $('#controls-help');
    if (!controls) return;
    const compact = innerWidth < 768 || window.matchMedia?.('(pointer: coarse)').matches;
    controls.textContent = compact
      ? 'Tap ground to move · Tap enemy to attack · Q: arc · F: loot · Bag: inventory'
      : 'WASD / click: move · Space: attack · Q: Dragon Arc · Shift: Ash Step · F: loot · I: inventory · H: home';
  }

  updateAudioButton() {
    const button = $('#audio-button');
    if (!button) return;
    const enabled = this.audio.enabled;
    button.textContent = enabled ? 'Sound' : 'Muted';
    button.title = enabled ? 'Mute sound' : 'Enable sound';
    button.setAttribute('aria-pressed', String(enabled));
    button.setAttribute('aria-label', enabled ? 'Mute sound' : 'Enable sound');
  }

  buildInventoryUI() {
    const grid = $('#inventory-grid');
    if (!grid) return;
    grid.replaceChildren();
    for (let index = 0; index < 20; index += 1) {
      const slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'inventory-slot';
      slot.dataset.slot = String(index);
      slot.setAttribute('aria-label', 'Empty inventory slot');
      slot.addEventListener('dblclick', () => this.activateInventorySlot(index));
      slot.addEventListener('click', () => slot.focus());
      slot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activateInventorySlot(index);
        } else if (event.shiftKey && /^Digit[1-8]$/.test(event.code)) {
          event.preventDefault();
          const item = this.inventory.slots[index];
          if (item?.itemKey) this.setQuickslot(Number(event.code.slice(-1)) - 1, item.itemKey);
          else this.toast('This inventory slot is empty.');
        }
      });
      slot.addEventListener('focus', () => {
        const itemKey = this.inventory.slots[index]?.itemKey;
        if (itemKey) this.showTooltip(itemKey, { currentTarget: slot });
      });
      slot.addEventListener('blur', () => this.hideTooltip());
      slot.addEventListener('dragstart', (event) => {
        const item = this.inventory.slots[index];
        if (!item) {
          event.preventDefault();
          return;
        }
        event.dataTransfer?.setData('text/plain', `inventory:${index}`);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
      });
      slot.addEventListener('dragover', (event) => {
        if (event.dataTransfer?.types.includes('text/plain')) {
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        }
      });
      slot.addEventListener('drop', (event) => {
        event.preventDefault();
        const source = event.dataTransfer?.getData('text/plain') || '';
        const match = /^inventory:(\d+)$/.exec(source);
        if (!match) return;
        const fromSlot = Number(match[1]);
        if (fromSlot !== index) this.emitAction('inventory:move', { fromSlot, toSlot: index });
      });
      slot.addEventListener('mouseenter', (event) => this.showTooltip(this.inventory.slots[index]?.itemKey, event));
      slot.addEventListener('mouseleave', () => this.hideTooltip());
      grid.appendChild(slot);
    }
    document.querySelectorAll('.equipment-slot').forEach((slot) => {
      slot.type = 'button';
      slot.addEventListener('dblclick', () => this.unequipItem(slot.dataset.equipment));
      slot.addEventListener('click', () => slot.focus());
      slot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.unequipItem(slot.dataset.equipment);
        }
      });
      slot.addEventListener('focus', () => {
        const itemKey = this.inventory.equipment[slot.dataset.equipment];
        if (itemKey) this.showTooltip(itemKey, { currentTarget: slot });
      });
      slot.addEventListener('blur', () => this.hideTooltip());
      slot.addEventListener('mouseenter', (event) => this.showTooltip(this.inventory.equipment[slot.dataset.equipment], event));
      slot.addEventListener('mouseleave', () => this.hideTooltip());
    });
  }

  buildQuickbar() {
    const bar = $('#quickbar');
    if (!bar) return;
    bar.replaceChildren();
    for (let index = 0; index < 8; index += 1) {
      const slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'slot';
      slot.dataset.key = String(index + 1);
      slot.dataset.slot = String(index);
      slot.setAttribute('aria-keyshortcuts', String(index + 1));
      slot.setAttribute('aria-label', `Empty quick slot ${index + 1}`);
      slot.setAttribute('aria-disabled', 'false');
      slot.addEventListener('click', () => this.useQuickslot(index));
      slot.addEventListener('keydown', (event) => {
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          this.setQuickslot(index, null);
        }
      });
      slot.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        this.setQuickslot(index, null);
      });
      slot.addEventListener('dragover', (event) => {
        if (event.dataTransfer?.types.includes('text/plain')) {
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        }
      });
      slot.addEventListener('drop', (event) => {
        event.preventDefault();
        const source = event.dataTransfer?.getData('text/plain') || '';
        const match = /^inventory:(\d+)$/.exec(source);
        const item = match ? this.inventory.slots[Number(match[1])] : null;
        if (item?.itemKey) this.setQuickslot(index, item.itemKey);
      });
      bar.appendChild(slot);
    }
  }

  emitAction(event, payload) {
    if (!this.socket?.connected) {
      this.toast('The frontier is not connected.');
      return;
    }
    this.socket.timeout(2000).emit(event, payload, (error, result) => {
      if (error) {
        this.toast('The frontier did not answer. Try again.');
      } else if (result && result.ok === false) {
        this.toast(result.error || 'Action rejected.');
      }
    });
  }

  hideTooltip() {
    const tooltip = $('#item-tooltip');
    if (!tooltip) return;
    tooltip.replaceChildren();
    setVisible(tooltip, false);
    tooltip.setAttribute('aria-hidden', 'true');
  }

  tooltipPoint(event) {
    const target = event?.currentTarget;
    if (target instanceof HTMLElement) {
      const rect = target.getBoundingClientRect();
      return { x: rect.right + 10, y: rect.top };
    }
    return { x: Number.isFinite(event?.clientX) ? event.clientX + 12 : innerWidth / 2, y: Number.isFinite(event?.clientY) ? event.clientY + 12 : innerHeight / 2 };
  }

  showTooltip(itemKey, event = {}) {
    const item = ITEM_TEMPLATES[itemKey];
    const tooltip = $('#item-tooltip');
    if (!tooltip) return;
    if (!item) {
      this.hideTooltip();
      return;
    }
    tooltip.replaceChildren();
    const title = document.createElement('strong');
    title.textContent = item.name;
    const description = document.createElement('span');
    description.textContent = item.description;
    const stats = document.createElement('small');
    const statText = [
      item.attack ? `Attack +${item.attack}` : '',
      item.defense ? `Defence +${item.defense}` : '',
      item.maxHealth ? `Max health +${item.maxHealth}` : '',
      item.heal ? `Restores ${item.heal} health` : ''
    ].filter(Boolean).join(' · ');
    if (statText) stats.textContent = statText;
    tooltip.append(title, document.createElement('br'), description);
    if (statText) tooltip.append(document.createElement('br'), stats);
    const point = this.tooltipPoint(event);
    tooltip.style.left = `${Math.max(8, Math.min(innerWidth - 238, point.x))}px`;
    tooltip.style.top = `${Math.max(8, Math.min(innerHeight - 100, point.y))}px`;
    tooltip.setAttribute('aria-label', `${item.name}. ${item.description}`);
    tooltip.setAttribute('aria-hidden', 'false');
    setVisible(tooltip, true);
  }

  setQuickslot(index, itemKey) {
    if (!Number.isInteger(index) || index < 0 || index >= 8) return;
    this.emitAction('quickslot:set', { slot: index, itemKey: itemKey || null });
  }

  unequipItem(equipmentSlot) {
    if (!equipmentSlot) return;
    this.emitAction('inventory:unequip', { equipmentSlot });
  }

  setItemCooldown(itemKey, readyAt) {
    if (!itemKey || !Number.isFinite(readyAt)) return;
    this.itemCooldowns.set(itemKey, readyAt);
    this.updateCooldownVisuals();
  }

  requestItemUse(itemKey) {
    if (!itemKey) return;
    const readyAt = this.itemCooldowns.get(itemKey) || 0;
    if (readyAt > Date.now()) {
      this.toast(`Tonic ready in ${Math.max(0.1, (readyAt - Date.now()) / 1000).toFixed(1)}s.`);
      return;
    }
    if (!this.socket?.connected) {
      this.toast('The frontier is not connected.');
      return;
    }
    this.socket.timeout(2000).emit('item:use', { itemKey }, (error, result) => {
      if (error) {
        this.toast('The frontier did not answer. Try again.');
        return;
      }
      if (!result?.ok) {
        if (result?.cooldownUntil) this.setItemCooldown(itemKey, result.cooldownUntil);
        this.toast(result?.error || 'That item could not be used.');
        return;
      }
      if (result.cooldownUntil) this.setItemCooldown(itemKey, result.cooldownUntil);
    });
  }

  updateCooldownVisuals(now = Date.now()) {
    document.querySelectorAll('#quickbar .slot').forEach((element, index) => {
      const key = this.inventory.quickslots[index];
      const readyAt = key ? (this.itemCooldowns.get(key) || 0) : 0;
      const remaining = Math.max(0, readyAt - now);
      if (remaining <= 0 && key) this.itemCooldowns.delete(key);
      element.classList.toggle('cooling', remaining > 0);
      element.dataset.cooldown = remaining > 0 ? String(Math.ceil(remaining / 100) / 10) : '';
      element.setAttribute('aria-disabled', String(remaining > 0));
      element.setAttribute('aria-label', key ? `${ITEM_TEMPLATES[key]?.name || 'Quick slot'}; slot ${index + 1}${remaining > 0 ? `; cooling down for ${(remaining / 1000).toFixed(1)} seconds` : ''}` : `Empty quick slot ${index + 1}`);
    });
  }

  itemMarkup(element, itemKey, quantity = 1) {
    element.replaceChildren();
    element.removeAttribute('title');
    element.removeAttribute('data-item-key');
    const template = ITEM_TEMPLATES[itemKey];
    if (!template) {
      element.setAttribute('aria-label', element.classList.contains('slot') ? `Empty quick slot ${Number(element.dataset.key || 0)}` : 'Empty inventory slot');
      element.draggable = false;
      return;
    }
    const icon = document.createElement('span');
    icon.className = 'item-icon';
    icon.style.setProperty('--item-color', template.color);
    icon.dataset.kind = template.kind;
    icon.dataset.slot = template.slot || '';
    icon.textContent = template.kind === 'consumable' ? 'P' : template.slot === 'weapon' ? 'W' : template.slot === 'armor' ? 'A' : 'C';
    icon.setAttribute('aria-hidden', 'true');
    element.appendChild(icon);
    if (quantity > 1) {
      const count = document.createElement('span');
      count.className = 'quantity';
      count.textContent = String(quantity);
      element.appendChild(count);
    }
    element.title = template.name;
    element.dataset.itemKey = itemKey;
    element.setAttribute('aria-label', `${template.name}${quantity > 1 ? `, quantity ${quantity}` : ''}`);
    element.draggable = element.classList.contains('inventory-slot');
  }

  renderInventory() {
    document.querySelectorAll('.inventory-slot').forEach((element, index) => {
      const item = this.inventory.slots[index];
      this.itemMarkup(element, item?.itemKey, item?.quantity);
    });
    document.querySelectorAll('.equipment-slot').forEach((element) => {
      const label = document.createElement('small');
      const slotName = element.dataset.equipment[0].toUpperCase() + element.dataset.equipment.slice(1);
      const itemKey = this.inventory.equipment[element.dataset.equipment];
      this.itemMarkup(element, itemKey, 1);
      const tier = this.progression.upgradesBySlot?.[element.dataset.equipment] || 0;
      label.textContent = `${slotName}${tier ? ` · +${tier}` : ''}`;
      element.setAttribute('aria-label', itemKey ? `${slotName}: ${ITEM_TEMPLATES[itemKey]?.name || 'equipped item'}${tier ? `, tier ${tier}` : ''}` : `Empty ${slotName.toLowerCase()} equipment slot`);
      element.appendChild(label);
    });
    document.querySelectorAll('#quickbar .slot').forEach((element, index) => {
      const key = this.inventory.quickslots[index];
      const quantity = this.inventory.slots.filter((item) => item?.itemKey === key).reduce((sum, item) => sum + item.quantity, 0);
      this.itemMarkup(element, key, quantity);
      element.dataset.key = String(index + 1);
    });
    $('#inventory-gold').textContent = `${this.self.gold} Yang`;
    this.updateCooldownVisuals();
  }

  activateInventorySlot(index) {
    const item = this.inventory.slots[index];
    const template = item && ITEM_TEMPLATES[item.itemKey];
    if (!template) return;
    if (template.kind === 'consumable') this.requestItemUse(item.itemKey);
    else this.emitAction('inventory:equip', { inventorySlot: index, equipmentSlot: template.slot });
  }

  toggleInventory() {
    const panel = $('#inventory-panel');
    if (!panel) return;
    if (panel.classList.contains('hidden')) {
      this.openModal(panel, '#inventory-close');
      const entry = this.modalStack.find((item) => item.element === panel);
      if (entry) entry.returnFocus = $('#inventory-button');
      $('#inventory-button')?.setAttribute('aria-expanded', 'true');
    } else {
      this.closeModal(panel);
      $('#inventory-button')?.setAttribute('aria-expanded', 'false');
    }
  }
  useQuickslot(index) {
    const itemKey = this.inventory.quickslots[index];
    if (itemKey) this.requestItemUse(itemKey);
  }
  pickupNearest() {
    const item = this.entities.nearestItem(this.self.x, this.self.y);
    if (item) this.socket?.emit('loot:pickup', { itemId: item.id }, (result) => { if (result?.error) this.toast(result.error); });
  }
  toast(message) {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    $('#toast-layer').appendChild(toast);
    setTimeout(() => toast.remove(), this.reduceMotion ? 0 : 2500);
  }

  setupDiagnostics() {
    if ('PerformanceObserver' in window) {
      try {
        this.longTaskObserver = new PerformanceObserver((list) => {
          this.longTasks += list.getEntries().filter((entry) => entry.duration >= 50).length;
        });
        this.longTaskObserver.observe({ type: 'longtask', buffered: true });
      } catch {
        // Long Task API is intentionally optional across browsers.
      }
    }
    window.__METON3_METRICS__ = () => this.metrics();
    if (import.meta.env.MODE === 'test') {
      window.__METON3_TEST__ = {
        state: () => this.state,
        metrics: () => this.metrics(),
        position: () => ({ x: this.self.x, y: this.self.y }),
        enemyIds: () => [...this.entities.enemies.keys()].sort(),
        forcePosition: (x, y) => {
          this.self.x = x;
          this.self.y = y;
          this.self.authX = x;
          this.self.authY = y;
        }
      };
    }
  }

  connect() {
    this.socket = io({
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 3000
    });
    this.socket.on('connect', () => {
      this.hideConnectionScreen();
      if (this.state === 'reconnecting') this.state = 'playing';
    });
    this.socket.on('connect_error', (error) => {
      if (error.message === 'UNAUTHENTICATED') {
        location.reload();
        return;
      }
      this.showReconnect(error.message);
    });
    this.socket.on('disconnect', (reason) => {
      if (reason !== 'io client disconnect') this.showReconnect(reason);
    });
    this.socket.on('session:replaced', () => {
      this.state = 'replaced';
      connectionTitle.textContent = 'Session moved';
      connectionMessage.textContent = 'This character was opened in another browser.';
      setVisible(reloadButton, true);
      this.showConnectionScreen();
    });
    this.socket.on('world:init', (data) => this.onWorldInit(data));
    this.socket.on('world:snapshot', (data) => this.onSnapshot(data));
    this.socket.on('game:event', (event) => this.onGameEvent(event));
    this.socket.on('inventory:state', (state) => {
      this.inventory = state;
      if (state.upgrades) {
        this.progression.upgradesBySlot = { ...this.progression.upgradesBySlot, ...state.upgrades };
        this.self.upgrades = this.progression.upgradesBySlot;
      }
      this.renderInventory();
    });
    this.socket.on('progression:state', (state) => this.onProgression(state));
    this.socket.on('presence', ({ online }) => {
      $('#online-label').textContent = `${online} online`;
    });
    this.socket.on('leaderboard', (entries) => this.renderLeaderboard(entries));
    this.socket.on('chat:message', (message) => this.renderChatMessage(message));
    this.inputTimer = setInterval(() => this.sendInput(), 1000 / GAME_CONFIG.network.inputRate);
  }

  onWorldInit(data) {
    this.self = {
      ...this.self,
      ...data.self,
      authX: data.self.x,
      authY: data.self.y
    };
    this.onProgression(data.self.progression);
    this.self.embers = data.self.embers || this.progression.embers || 0;
    this.self.upgrades = data.self.upgrades || this.progression.upgradesBySlot;
    this.abilityReadyAt = {
      dragonArc: data.self.abilities?.dragonArc?.readyAt || 0,
      ashStep: data.self.abilities?.ashStep?.readyAt || 0
    };
    this.entities.setSelf(this.self.x, this.self.y);
    this.chunks.update(this.self.x, this.self.y);
    this.worldReady = true;
    loadingStatus.textContent = 'Building nearby terrain…';
    this.finishLoading();
  }

  onTerrainReady() {
    this.terrainReady = true;
    this.finishLoading();
  }

  finishLoading() {
    if (!this.worldReady || !this.terrainReady) return;
    if (this.state !== 'loading') return;
    this.state = 'playing';
    bootState = this.state;
    setVisible(loadingScreen, false);
    setVisible(hud, true);
    setVisible(chat, true);
  }

  onProgression(state) {
    if (!state || typeof state !== 'object') return;
    this.progression = {
      ...this.progression,
      ...state,
      upgradesBySlot: { ...this.progression.upgradesBySlot, ...(state.upgradesBySlot || {}) }
    };
    this.self.progression = this.progression;
    this.self.embers = this.progression.embers || 0;
    this.self.upgrades = this.progression.upgradesBySlot;
    this.renderProgression();
  }

  renderProgression() {
    const objective = this.progression.objective;
    const panel = $('#quest-panel');
    if (panel) {
      setVisible(panel, true);
      panel.setAttribute('aria-hidden', 'false');
      $('#quest-title').textContent = objective?.title || 'Frontier mastery';
      $('#quest-description').textContent = objective?.text || 'The frontier cycle is complete. Return when you are ready for a harder hunt.';
      const value = objective?.value || 0;
      const target = objective?.target || 1;
      const progress = $('#quest-progress');
      progress?.setAttribute('aria-valuemax', String(target));
      progress?.setAttribute('aria-valuenow', String(value));
      progress?.setAttribute('aria-valuetext', `${value} of ${target}`);
      $('#quest-progress-fill').style.width = `${Math.min(100, value / target * 100)}%`;
      $('#quest-status').textContent = objective?.hint || 'Your next mark is waiting.';
      panel.dataset.questState = objective?.complete ? 'ready' : 'active';
    }
    const mastery = $('#mastery-result');
    const masteryReady = objective?.key === 'choose_mastery';
    setVisible(mastery, masteryReady);
    mastery?.setAttribute('aria-hidden', String(!masteryReady));
    if (masteryReady) {
      $('#mastery-result-message').textContent = 'The Warden is defeated. Claim a mastery mark at Crimson Rest.';
      $('#mastery-result-value').textContent = `Mastery ${this.progression.mastery || 0} → ${Number(this.progression.mastery || 0) + 1}`;
    }
  }

  renderForge() {
    const panel = $('#forge-panel');
    if (!panel) return;
    const distance = Math.hypot(this.self.x - 620, this.self.y - 470);
    const nearby = distance <= 260;
    setVisible(panel, nearby);
    panel.setAttribute('aria-hidden', String(!nearby));
    if (!nearby) return;
    const slot = ['weapon', 'armor', 'charm'].find((key) => this.inventory.equipment[key]);
    const tier = this.progression.upgradesBySlot?.[slot] || 0;
    const costs = [2, 4, 7];
    const cost = costs[tier];
    const action = $('#forge-action');
    if (!slot) {
      $('#forge-status').textContent = 'Equip a weapon, armor, or charm first.';
      action.disabled = true;
      return;
    }
    const name = ITEM_TEMPLATES[this.inventory.equipment[slot]]?.name || slot;
    $('#forge-status').textContent = tier >= costs.length
      ? `${name} has reached tier III.`
      : `${name} · tier ${tier} · ${cost} Ember Shards required.`;
    action.textContent = tier >= costs.length ? 'Tempered' : `Temper ${slot}`;
    action.disabled = tier >= costs.length || (this.progression.embers || 0) < cost || this.progression.step < 3;
    action.setAttribute('aria-label', `Temper ${name} for ${cost || 0} Ember Shards`);
    action.dataset.equipmentSlot = slot;
  }

  renderAbility(now = Date.now()) {
    const button = $('#active-skill');
    if (!button) return;
    const readyAt = this.abilityReadyAt.dragonArc || 0;
    const remaining = Math.max(0, readyAt - now);
    const cooldown = $('#active-skill-cooldown');
    cooldown.textContent = remaining > 0 ? `${(remaining / 1000).toFixed(1)}s` : 'Ready';
    button.classList.toggle('cooling', remaining > 0);
    button.setAttribute('aria-pressed', String(this.skillQueued));
    button.setAttribute('aria-label', remaining > 0 ? `${ABILITIES.dragonArc.name}, cooling down` : `Use ${ABILITIES.dragonArc.name}`);
  }

  requestForge() {
    const equipmentSlot = $('#forge-action')?.dataset.equipmentSlot;
    if (!equipmentSlot) return;
    this.emitAction('forge:upgrade', { equipmentSlot });
  }

  claimMastery() {
    this.socket?.timeout(2000).emit('mastery:claim', {}, (error, result) => {
      if (error) this.toast('The frontier did not answer.');
      else if (!result?.ok) this.toast(result?.error || 'Mastery is not ready.');
    });
  }

  onSnapshot(data) {
    if (!data?.self) return;
    this.self.authX = data.self.x;
    this.self.authY = data.self.y;
    this.self.health = data.self.health;
    this.self.maxHealth = data.self.maxHealth;
    this.self.level = data.self.level;
    this.self.xp = data.self.xp;
    this.self.xpToLevel = data.self.xpToLevel;
    this.self.gold = data.self.gold;
    this.self.embers = data.self.embers || 0;
    this.self.upgrades = data.self.upgrades || this.self.upgrades;
    this.self.attack = data.self.attack;
    this.self.defense = data.self.defense;
    if (data.self.progression) this.onProgression(data.self.progression);
    this.abilityReadyAt = {
      dragonArc: data.self.abilities?.dragonArc?.readyAt || this.abilityReadyAt.dragonArc,
      ashStep: data.self.abilities?.ashStep?.readyAt || this.abilityReadyAt.ashStep
    };
    const error = Math.hypot(this.self.x - this.self.authX, this.self.y - this.self.authY);
    if (error > 140) {
      this.self.x = this.self.authX;
      this.self.y = this.self.authY;
    } else {
      this.self.x += (this.self.authX - this.self.x) * 0.3;
      this.self.y += (this.self.authY - this.self.y) * 0.3;
    }
    this.entities.updateRemotePlayers(data.players || []);
    this.entities.updateEnemies(data.enemies || []);
    this.entities.updateItems(data.items || []);
    if (this.selectedTargetId && !this.entities.enemies.has(this.selectedTargetId)) {
      this.selectedTargetId = null;
      this.entities.select(null);
    }
    if (data.self.dead) {
      this.state = 'dead';
      this.showDeathScreen();
    }
  }

  sendInput() {
    if (!this.socket?.connected || !['playing', 'loading'].includes(this.state)) return;
    const input = this.inputVector();
    this.sequence += 1;
    this.socket.volatile.emit('player:input', {
      seq: this.sequence,
      moveX: input.x,
      moveY: input.y,
      attackPressed: this.attackQueued,
      skillPressed: this.skillQueued,
      dashPressed: this.dashQueued,
      targetId: this.selectedTargetId
    });
    this.attackQueued = false;
    this.skillQueued = false;
    this.dashQueued = false;
  }

  inputVector() {
    let screenX = 0;
    let screenY = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) screenX -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) screenX += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) screenY += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) screenY -= 1;
    let x = this.moveRight.x * screenX + this.moveUp.x * screenY;
    let y = this.moveRight.y * screenX + this.moveUp.y * screenY;
    if (screenX || screenY) this.clickDestination = null;
    if (!screenX && !screenY && this.clickDestination) {
      const targetEnemy = this.selectedTargetId && this.entities.enemies.get(this.selectedTargetId);
      if (targetEnemy) {
        this.clickDestination.x = targetEnemy.x;
        this.clickDestination.y = targetEnemy.y;
      }
      const dx = this.clickDestination.x - this.self.x;
      const dy = this.clickDestination.y - this.self.y;
      const distance = Math.hypot(dx, dy);
      if (distance <= this.clickDestination.stopAt) {
        this.clickDestination = null;
        if (targetEnemy && performance.now() - this.lastAutoAttack > GAME_CONFIG.player.attackCooldownMs) {
          this.attackQueued = true;
          this.lastAutoAttack = performance.now();
        }
      } else {
        x = dx / distance;
        y = dy / distance;
      }
    }
    if (!screenX && !screenY && !this.clickDestination && this.selectedTargetId) {
      const targetEnemy = this.entities.enemies.get(this.selectedTargetId);
      if (targetEnemy && Math.hypot(targetEnemy.x - this.self.x, targetEnemy.y - this.self.y) <= GAME_CONFIG.player.attackRange && performance.now() - this.lastAutoAttack > GAME_CONFIG.player.attackCooldownMs) {
        this.attackQueued = true;
        this.lastAutoAttack = performance.now();
      }
    }
    const length = Math.hypot(x, y);
    if (length > 1) {
      x /= length;
      y /= length;
    }
    return { x, y };
  }

  predict(dt) {
    if (this.state !== 'playing') return;
    const input = this.inputVector();
    const nextX = this.self.x + input.x * GAME_CONFIG.player.speed * dt;
    const nextY = this.self.y + input.y * GAME_CONFIG.player.speed * dt;
    if (terrainAt(nextX, nextY).walkable && nextX >= GAME_CONFIG.zone.minX && nextX <= GAME_CONFIG.zone.maxX && nextY >= GAME_CONFIG.zone.minY && nextY <= GAME_CONFIG.zone.maxY) {
      this.self.x = nextX;
      this.self.y = nextY;
    }
  }

  returnHome() {
    if (!this.socket?.connected || this.state !== 'playing') return;
    this.socket.timeout(2000).emit('player:returnHome', () => {});
  }

  onGameEvent(event) {
    if (!event?.type) return;
    if (event.type === 'death') {
      this.state = 'dead';
      this.showDeathScreen();
    } else if (event.type === 'respawn' || event.type === 'home') {
      this.state = 'playing';
      this.self.x = event.x;
      this.self.y = event.y;
      this.self.authX = event.x;
      this.self.authY = event.y;
      this.hideDeathScreen();
    } else if (event.type === 'damageEnemy') {
      event.source === 'dragonArc' ? this.audio.skill() : this.audio.sword();
      const enemy = this.entities.enemies.get(event.enemyId);
      if (enemy) this.floatingText(enemy.x, enemy.y, `-${event.amount}`, '#ff756b');
    } else if (event.type === 'skill') {
      this.abilityReadyAt.dragonArc = event.readyAt || Date.now() + ABILITIES.dragonArc.cooldownMs;
      this.audio.skill();
      this.toast(`${ABILITIES.dragonArc.name} unleashed.`);
    } else if (event.type === 'dash') {
      this.abilityReadyAt.ashStep = event.readyAt || Date.now() + ABILITIES.ashStep.cooldownMs;
      this.audio.dash();
    } else if (event.type === 'bossLocked') {
      this.toast('The Warden is sealed. Follow the frontier objective first.');
    } else if (event.type === 'enemyDefeated') {
      this.floatingText(this.self.x, this.self.y, `+${event.xp} XP`, '#6fe8ff');
      if (event.levelled) { this.floatingText(this.self.x, this.self.y, 'LEVEL UP!', '#ffd66b'); this.audio.level(); }
    } else if (event.type === 'damagePlayer') {
      this.audio.hit();
      this.floatingText(this.self.x, this.self.y, `-${event.amount}`, '#ff756b');
    } else if (event.type === 'pickup') {
      const label = event.itemType === 'gold' ? `+${event.amount} Yang` : event.itemType === 'ember' ? `+${event.amount} Ember Shard${event.amount === 1 ? '' : 's'}` : `${ITEM_TEMPLATES[event.itemKey]?.name || 'Item'} acquired`;
      this.self.embers = event.embers ?? this.self.embers;
      this.floatingText(this.self.x, this.self.y, label, event.itemType === 'gold' ? '#ffd66b' : '#73ef91');
      this.audio.pickup();
    } else if (event.type === 'objectiveComplete') {
      this.toast(`${event.title} complete.`);
      this.audio.level();
    } else if (event.type === 'forge') {
      this.toast(`${ITEM_TEMPLATES[event.itemKey]?.name || 'Gear'} tempered to tier ${event.tier}.`);
      this.self.embers = event.embers;
      this.audio.level();
    } else if (event.type === 'bossPhase') {
      this.toast(`The Crimson Warden enters phase ${event.phase}.`);
      this.audio.boss();
      setVisible($('#boss-alert'), true);
    } else if (event.type === 'bossDefeated') {
      this.toast('The Crimson Warden falls. Return to camp for mastery.');
      this.audio.level();
      setVisible($('#boss-alert'), false);
    } else if (event.type === 'mastery') {
      this.self.embers = event.embers;
      this.toast(`Mastery mark ${event.rank} claimed.`);
      this.audio.level();
      setVisible($('#mastery-result'), false);
    } else if (event.type === 'itemUsed') {
      this.self.health = event.health;
      if (event.cooldownUntil) this.setItemCooldown(event.itemKey, event.cooldownUntil);
      this.floatingText(this.self.x, this.self.y, '+60 HP', '#73ef91');
      this.audio.pickup();
    } else if (event.type === 'monolithWave') {
      this.toast(`The monolith fractures — corruption wave ${event.phase}`);
      this.audio.monolith();
    }
  }

  floatingText(x, y, text, color) {
    const point = new THREE.Vector3(x, y, terrainAt(x, y).height + 55).project(this.camera);
    const element = document.createElement('div');
    element.className = 'floating-text';
    element.textContent = text;
    element.style.color = color;
    element.style.left = `${(point.x * 0.5 + 0.5) * innerWidth}px`;
    element.style.top = `${(-point.y * 0.5 + 0.5) * innerHeight}px`;
    $('#floating-layer').appendChild(element);
    setTimeout(() => element.remove(), 950);
  }

  renderChatMessage(message) {
    const line = document.createElement('div');
    line.className = 'chat-message';
    const name = document.createElement('span');
    name.className = 'chat-name';
    name.textContent = `${message.name} · Lv.${message.level}`;
    line.append(name, document.createTextNode(`: ${message.message}`));
    const messages = $('#chat-messages');
    messages.appendChild(line);
    while (messages.children.length > 50) messages.firstElementChild.remove();
    messages.scrollTop = messages.scrollHeight;
  }

  renderLeaderboard(entries) {
    const list = $('#leaderboard');
    list.replaceChildren();
    for (const entry of entries.slice(0, 10)) {
      const item = document.createElement('li');
      item.textContent = `${entry.name} · Lv.${entry.level}`;
      list.appendChild(item);
    }
  }

  showReconnect(message) {
    if (['replaced', 'graphics-lost'].includes(this.state)) return;
    this.state = 'reconnecting';
    connectionTitle.textContent = 'Reconnecting…';
    connectionMessage.textContent = message || 'Holding your place in the world.';
    setVisible(reloadButton, false);
    this.showConnectionScreen();
  }

  frame(now) {
    const rawDt = now - this.lastFrame;
    const dt = Math.min(0.05, Math.max(0, rawDt / 1000));
    this.lastFrame = now;
    this.frameTimes.push(rawDt);
    if (this.frameTimes.length > 600) this.frameTimes.shift();

    this.predict(dt);
    this.entities.setSelf(this.self.x, this.self.y);
    this.entities.render(dt, now / 1000);
    this.chunks.update(this.self.x, this.self.y);
    this.chunks.uploadOne();
    this.camera.position.set(
      this.self.x + this.isoDirection.x * 900,
      this.self.y + this.isoDirection.y * 900,
      terrainAt(this.self.x, this.self.y).height + this.isoDirection.z * 900
    );
    this.camera.lookAt(this.self.x, this.self.y, terrainAt(this.self.x, this.self.y).height);
    this.renderer.render(this.scene, this.camera);
    this.updateHUD(now);
    this.raf = requestAnimationFrame((time) => this.frame(time));
  }

  updateHUD(now) {
    if (now - this.lastHudUpdate < 100) return;
    this.lastHudUpdate = now;
    $('#level-label').textContent = `Level ${this.self.level}`;
    $('#gold-label').textContent = `${this.self.gold} Yang · ${this.self.embers || 0} Shards`;
    $('#inventory-gold').textContent = `${this.self.gold} Yang · ${this.self.embers || 0} Shards`;
    const healthPercent = Math.max(0, Math.min(100, this.self.health / this.self.maxHealth * 100));
    const xpPercent = Math.max(0, Math.min(100, this.self.xp / this.self.xpToLevel * 100));
    $('#health-fill').style.width = `${healthPercent}%`;
    $('#health-label').textContent = `${Math.ceil(this.self.health)} / ${this.self.maxHealth}`;
    const healthTrack = $('.health-track');
    healthTrack?.setAttribute('aria-valuemax', String(this.self.maxHealth));
    healthTrack?.setAttribute('aria-valuenow', String(Math.ceil(this.self.health)));
    healthTrack?.setAttribute('aria-valuetext', `${Math.ceil(this.self.health)} / ${this.self.maxHealth}`);
    $('#xp-fill').style.height = `${xpPercent}%`;
    $('#xp-level').textContent = String(this.self.level);
    $('#xp-label').textContent = `${this.self.xp} / ${this.self.xpToLevel} XP`;
    const xpOrb = $('.xp-orb');
    xpOrb?.setAttribute('aria-valuemax', String(this.self.xpToLevel));
    xpOrb?.setAttribute('aria-valuenow', String(this.self.xp));
    xpOrb?.setAttribute('aria-valuetext', `${this.self.xp} / ${this.self.xpToLevel} XP`);
    this.updateCooldownVisuals(now);
    this.renderProgression();
    this.renderForge();
    this.renderAbility(now);
    const metrics = this.metrics();
    perfHud.textContent =
      `FPS ${metrics.fps.toFixed(0)}  p95 ${metrics.p95FrameMs.toFixed(1)}ms\n` +
      `draws ${metrics.drawCalls}  tris ${metrics.triangles}\n` +
      `chunks ${metrics.chunks} enemies ${metrics.enemies} long ${metrics.longTasks}`;
    this.drawMinimap();
    this.drawWorldLabels();
  }

  enemyName(type) {
    return ({ ashHound: 'Ash Hound', ridgeWolf: 'Ridge Wolf', redTuskRaider: 'Red-Tusk Raider', dragonMonolith: 'Dragon Vein Monolith', crimsonWarden: 'Crimson Warden' })[type] || 'Corrupted Creature';
  }

  screenPoint(x, y, z = 45) {
    const point = new THREE.Vector3(x, y, terrainAt(x, y).height + z).project(this.camera);
    return { x: (point.x * 0.5 + 0.5) * innerWidth, y: (-point.y * 0.5 + 0.5) * innerHeight, visible: point.z > -1 && point.z < 1 };
  }

  drawWorldLabels() {
    const layer = $('#world-labels');
    layer.replaceChildren();
    const target = this.entities.enemies.get(this.selectedTargetId);
    setVisible($('#target-panel'), Boolean(target));
    if (target) {
      $('#target-name').textContent = this.enemyName(target.type);
      $('#target-level').textContent = `Level ${target.level}${target.type === 'dragonMonolith' ? ` · Corruption ${target.phase}/3` : ''}`;
      const targetPercent = Math.max(0, Math.min(100, target.health / target.maxHealth * 100));
      $('#target-health').style.width = `${targetPercent}%`;
      const targetProgress = $('#target-panel [role="progressbar"]');
      targetProgress?.setAttribute('aria-valuenow', String(Math.ceil(targetPercent)));
      const point = this.screenPoint(target.x, target.y, target.type === 'dragonMonolith' ? 100 : 58);
      if (point.visible) this.addWorldLabel(layer, point, `${this.enemyName(target.type)} · Lv.${target.level}`, 'target');
      const boss = $('#boss-alert');
      if (target.type === 'crimsonWarden') {
        const percent = Math.max(0, Math.min(100, target.health / target.maxHealth * 100));
        setVisible(boss, true);
        boss?.setAttribute('aria-hidden', 'false');
        $('#boss-alert-title').textContent = this.enemyName(target.type);
        $('#boss-alert-message').textContent = `Phase ${target.phase || 0} · The quarry Warden watches your every step.`;
        $('#boss-alert-health-fill').style.width = `${percent}%`;
        $('#boss-alert-health-label').textContent = `${Math.ceil(target.health)} / ${target.maxHealth}`;
        $('#boss-alert-health').setAttribute('aria-valuenow', String(Math.ceil(percent)));
        $('#boss-alert-health').setAttribute('aria-valuetext', `${Math.ceil(percent)} percent health`);
      }
    }
    for (const item of this.entities.items.values()) {
      if (item.type === 'gold' || Math.hypot(item.x - this.self.x, item.y - this.self.y) > 350) continue;
      const point = this.screenPoint(item.x, item.y, 28);
      const template = ITEM_TEMPLATES[item.itemKey];
      const label = template?.name || (item.type === 'ember' ? 'Ember Shard' : null);
      if (point.visible && label) this.addWorldLabel(layer, point, `${label}  [F]`, 'loot');
    }
  }

  addWorldLabel(layer, point, text, className) {
    const label = document.createElement('span');
    label.className = `world-label ${className}`;
    label.textContent = text;
    label.style.left = `${point.x}px`;
    label.style.top = `${point.y}px`;
    layer.appendChild(label);
  }

  drawMinimap() {
    const canvas = $('#minimap');
    const context = canvas.getContext('2d');
    const size = canvas.width;
    context.clearRect(0, 0, size, size);
    context.fillStyle = '#243424';
    context.fillRect(0, 0, size, size);
    context.strokeStyle = '#80633c';
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(size * 0.5, size);
    context.lineTo(size * 0.5, size * 0.5);
    context.lineTo(size * 0.82, size * 0.5);
    context.moveTo(size * 0.5, size * 0.5);
    context.lineTo(size * 0.72, size * 0.18);
    context.stroke();
    const mapPoint = (x, y) => ({ x: (x - GAME_CONFIG.zone.minX) / (GAME_CONFIG.zone.maxX - GAME_CONFIG.zone.minX) * size, y: size - (y - GAME_CONFIG.zone.minY) / (GAME_CONFIG.zone.maxY - GAME_CONFIG.zone.minY) * size });
    for (const landmark of LANDMARKS) {
      const point = mapPoint(landmark.x, landmark.y);
      context.fillStyle = landmark.kind === 'monolith' || landmark.kind === 'boss' ? '#e33b34' : landmark.kind === 'village' ? '#f2c66d' : '#c7b386';
      context.beginPath();
      context.arc(point.x, point.y, landmark.kind === 'village' ? 5 : 3, 0, Math.PI * 2);
      context.fill();
    }
    for (const enemy of this.entities.enemies.values()) {
      if (Math.hypot(enemy.x - this.self.x, enemy.y - this.self.y) > 650) continue;
      const point = mapPoint(enemy.x, enemy.y);
      context.fillStyle = enemy.type === 'dragonMonolith' || enemy.type === 'crimsonWarden' ? '#ff3c35' : '#9b332b';
      context.fillRect(point.x - 1.5, point.y - 1.5, 3, 3);
    }
    const player = mapPoint(this.self.x, this.self.y);
    context.fillStyle = '#79dcff';
    context.beginPath();
    context.moveTo(player.x, player.y - 6);
    context.lineTo(player.x - 4, player.y + 5);
    context.lineTo(player.x + 4, player.y + 5);
    context.closePath();
    context.fill();
  }

  metrics() {
    const frames = [...this.frameTimes].filter((value) => value > 0).sort((a, b) => a - b);
    const median = frames[Math.floor(frames.length * 0.5)] || 16.7;
    const p95 = frames[Math.floor(frames.length * 0.95)] || median;
    return {
      state: this.state,
      fps: 1000 / median,
      p95FrameMs: p95,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
      chunks: this.chunks.loaded.size,
      enemies: this.entities.enemies.size,
      items: this.entities.items.size,
      longTasks: this.longTasks,
      uptimeMs: performance.now() - this.startedAt
    };
  }

  resize() {
    const aspect = innerWidth / innerHeight;
    this.camera.left = -this.viewSize * aspect;
    this.camera.right = this.viewSize * aspect;
    this.camera.top = this.viewSize;
    this.camera.bottom = -this.viewSize;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, innerWidth < 768 ? 1.25 : 1.75));
  }

  dispose() {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.inputTimer) clearInterval(this.inputTimer);
    this.socket?.disconnect();
    this.chunks.dispose();
    this.entities.dispose();
    this.decor.dispose();
    this.audio.dispose();
    this.renderer.dispose();
    this.longTaskObserver?.disconnect();
    removeEventListener('keydown', this.onKeyDown);
    removeEventListener('keyup', this.onKeyUp);
    if (this.onResize) removeEventListener('resize', this.onResize);
    if (this.onModalKeyDownListener) document.removeEventListener('keydown', this.onModalKeyDownListener, true);
  }
}

async function startGame(player) {
  if (app) app.dispose();
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  bootState = 'loading';
  setVisible(authScreen, false);
  setVisible(loadingScreen, true);
  loadingStatus.textContent = 'Connecting to the shared world…';
  try {
    app = new GameClient(player);
  } catch (error) {
    app = null;
    bootState = 'graphics-unavailable';
    connectionTitle.textContent = '3D graphics unavailable';
    connectionMessage.textContent =
      'WebGL could not start. Enable hardware acceleration or update your browser and graphics driver.';
    setVisible(loadingScreen, false);
    setVisible(reloadButton, true);
    setVisible(connectionScreen, true);
    connectionScreen.setAttribute('aria-hidden', 'false');
    reportClientError(error, bootState);
  }
}

async function authenticate(action) {
  authError.textContent = '';
  const formData = new FormData(authForm);
  const credentials = {
    username: String(formData.get('username') || '').trim(),
    password: String(formData.get('password') || '')
  };
  const buttons = authForm.querySelectorAll('button');
  buttons.forEach((button) => { button.disabled = true; });
  try {
    const body = await api(`/api/auth/${action}`, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
    await startGame(body.player);
  } catch (error) {
    authError.textContent = error.message;
  } finally {
    buttons.forEach((button) => { button.disabled = false; });
  }
}

authForm.addEventListener('submit', (event) => {
  event.preventDefault();
  authenticate('login');
});
authForm.querySelector('[data-action="register"]').addEventListener('click', () => authenticate('register'));
$('#logout-button').addEventListener('click', async () => {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } catch {
    // Reloading still clears unusable local state; the server cookie remains protected.
  } finally {
    app?.dispose();
    location.reload();
  }
});

async function boot() {
  try {
    const session = await api('/api/auth/session');
    if (!session.authenticated) {
      bootState = 'auth';
      setVisible(authScreen, true);
      setVisible(loadingScreen, false);
      return;
    }
    await startGame(session.player);
  } catch {
    bootState = 'auth';
    setVisible(authScreen, true);
    setVisible(loadingScreen, false);
  }
}

addEventListener('error', (event) => reportClientError(event.error || new Error(event.message)));
addEventListener('unhandledrejection', (event) => reportClientError(event.reason));
addEventListener('resize', updateResponsiveRequirement);
updateResponsiveRequirement();

boot();
